import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Save, Upload, Search, MapPin, CheckCircle, AlertCircle, Camera } from 'lucide-react';
import airportData from '../../data/airports.json';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const airportDatabase = airportData.airports;

const CompanySettings = () => {
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [fetchingCoords, setFetchingCoords] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  
  const [companyForm, setCompanyForm] = useState({
    name: '',
    icao_code: '',
    iata_code: '',
    latitude: '',
    longitude: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    website: '',
    timezone: 'America/Chicago',
    landing_fee_rate: 0,
    fuel_surcharge_percent: 0,
    tax_rate: 0
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/company/settings`);
      if (response.data) {
        setCompany(response.data);
        setCompanyForm({
          name: response.data.name || '',
          icao_code: response.data.icao_code || '',
          iata_code: response.data.iata_code || '',
          latitude: response.data.latitude || '',
          longitude: response.data.longitude || '',
          address: response.data.address || '',
          city: response.data.city || '',
          state: response.data.state || '',
          zip_code: response.data.zip_code || '',
          phone: response.data.phone || '',
          email: response.data.email || '',
          website: response.data.website || '',
          timezone: response.data.timezone || 'America/Chicago',
          landing_fee_rate: response.data.landing_fee_rate || 0,
          fuel_surcharge_percent: response.data.fuel_surcharge_percent || 0,
          tax_rate: response.data.tax_rate || 0
        });
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const response = await axios.put(`${API_BASE}/company/settings`, companyForm);
      setCompany(response.data);
      setSaveStatus({ type: 'success', message: 'Company settings saved successfully!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving company:', error);
      setSaveStatus({ type: 'error', message: 'Error saving company settings' });
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    setUploadingLogo(true);

    try {
      const response = await axios.post(`${API_BASE}/company/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCompany({ ...company, logo_url: response.data.logo_url });
      setSaveStatus({ type: 'success', message: 'Logo uploaded successfully!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setSaveStatus({ type: 'error', message: 'Error uploading logo' });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleGetCoordinates = async () => {
    const icaoCode = companyForm.icao_code?.toUpperCase().trim();
    
    if (!icaoCode) {
      setSaveStatus({ type: 'error', message: 'Please enter an ICAO code first' });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    setFetchingCoords(true);
    
    try {
      if (airportDatabase[icaoCode]) {
        const airport = airportDatabase[icaoCode];
        setCompanyForm({
          ...companyForm,
          latitude: airport.lat.toFixed(6),
          longitude: airport.lon.toFixed(6),
          city: airport.city || companyForm.city,
          state: airport.state || companyForm.state
        });
        setSaveStatus({ type: 'success', message: `Coordinates found for ${airport.name}` });
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus({ type: 'error', message: `Airport "${icaoCode}" not found in database` });
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error:', error);
      setSaveStatus({ type: 'error', message: 'Error fetching coordinates' });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setFetchingCoords(false);
    }
  };

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Company Information</h3>
        <div className="flex items-center gap-3">
          {saveStatus && (
            <span className={`text-sm flex items-center gap-1 ${saveStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {saveStatus.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {saveStatus.message}
            </span>
          )}
          <button
            onClick={handleSaveCompany}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition text-white disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo Upload */}
        <div className="lg:col-span-1">
          <div className="bg-gray-900/50 rounded-lg p-6 text-center">
            <div className="mb-4">
              {company?.logo_url ? (
                <img 
                  src={`${API_BASE}${company.logo_url}`} 
                  alt="Company Logo"
                  className="w-32 h-32 object-contain mx-auto rounded-lg"
                />
              ) : (
                <div className="w-32 h-32 mx-auto bg-gray-700 rounded-lg flex items-center justify-center">
                  <Building2 className="w-12 h-12 text-gray-500" />
                </div>
              )}
            </div>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleLogoUpload(e.target.files[0])}
                className="hidden"
              />
              <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition">
                {uploadingLogo ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                <span className="text-sm">Upload Logo</span>
              </div>
            </label>
            <p className="text-xs text-gray-500 mt-2">Used on invoices, emails, and reports</p>
          </div>
        </div>

        {/* Company Details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Company Name *</label>
              <input
                type="text"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">ICAO Code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={companyForm.icao_code}
                  onChange={(e) => setCompanyForm({ ...companyForm, icao_code: e.target.value.toUpperCase() })}
                  placeholder="KBOE"
                  className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 uppercase"
                />
                <button
                  onClick={handleGetCoordinates}
                  disabled={fetchingCoords}
                  className="px-3 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition disabled:opacity-50"
                  title="Get GPS coordinates from ICAO code"
                >
                  {fetchingCoords ? (
                    <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Latitude</label>
              <input
                type="text"
                value={companyForm.latitude}
                onChange={(e) => setCompanyForm({ ...companyForm, latitude: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Longitude</label>
              <input
                type="text"
                value={companyForm.longitude}
                onChange={(e) => setCompanyForm({ ...companyForm, longitude: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">IATA Code</label>
              <input
                type="text"
                value={companyForm.iata_code}
                onChange={(e) => setCompanyForm({ ...companyForm, iata_code: e.target.value.toUpperCase() })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white uppercase"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Timezone</label>
              <select
                value={companyForm.timezone}
                onChange={(e) => setCompanyForm({ ...companyForm, timezone: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              >
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Phoenix">Arizona (MST)</option>
                <option value="America/Anchorage">Alaska (AKST)</option>
                <option value="Pacific/Honolulu">Hawaii (HST)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Address</label>
            <input
              type="text"
              value={companyForm.address}
              onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">City</label>
              <input
                type="text"
                value={companyForm.city}
                onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">State</label>
              <input
                type="text"
                value={companyForm.state}
                onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">ZIP Code</label>
              <input
                type="text"
                value={companyForm.zip_code}
                onChange={(e) => setCompanyForm({ ...companyForm, zip_code: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Phone</label>
              <input
                type="tel"
                value={companyForm.phone}
                onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <input
                type="email"
                value={companyForm.email}
                onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Website</label>
            <input
              type="url"
              value={companyForm.website}
              onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;